import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
    Ticket,
    Gift,
    Loader2,
    CheckCircle2,
    Sparkles,
    CreditCard,
    ArrowRight
} from "lucide-react";

const Promo = () => {
    const { t } = useLanguage();
    const { user, loading: authLoading } = useAuth();
    const { toast } = useToast();
    const navigate = useNavigate();

    const [couponCode, setCouponCode] = useState("");
    const [isRedeeming, setIsRedeeming] = useState(false);
    const [redeemResult, setRedeemResult] = useState<{
        success: boolean;
        message: string;
        credits?: number;
    } | null>(null);

    const handleRedeem = async () => {
        if (!user) {
            toast({
                title: t("กรุณาเข้าสู่ระบบ", "Please login"),
                description: t("คุณต้องเข้าสู่ระบบก่อนใช้คูปอง", "You need to login before using a coupon"),
                variant: "destructive",
            });
            navigate("/login");
            return;
        }

        if (!couponCode.trim()) {
            toast({
                title: t("กรุณากรอกรหัสคูปอง", "Please enter coupon code"),
                variant: "destructive",
            });
            return;
        }

        setIsRedeeming(true);
        setRedeemResult(null);

        try {
            const { data, error } = await (supabase as any).rpc("redeem_coupon", {
                coupon_code: couponCode.trim()
            });

            if (error) throw error;

            setRedeemResult(data);

            if (data.success) {
                toast({
                    title: "🎉 " + t("สำเร็จ!", "Success!"),
                    description: data.message,
                });
                setCouponCode("");
            } else {
                toast({
                    title: t("ไม่สำเร็จ", "Failed"),
                    description: data.message,
                    variant: "destructive",
                });
            }
        } catch (err: any) {
            console.error("Redeem error:", err);
            toast({
                title: t("เกิดข้อผิดพลาด", "Error"),
                description: err.message,
                variant: "destructive",
            });
        } finally {
            setIsRedeeming(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
            <Navbar />

            <main className="pt-24 pb-16">
                <div className="container mx-auto px-4 max-w-xl">
                    {/* Header */}
                    <div className="text-center mb-8">
                        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-purple-500/20 mb-4">
                            <Gift className="h-10 w-10 text-primary" />
                        </div>
                        <h1 className="text-3xl md:text-4xl font-heading font-bold mb-2">
                            <span className="text-gradient">{t("รับเครดิตฟรี", "Get Free Credits")}</span>
                        </h1>
                        <p className="text-muted-foreground">
                            {t("กรอกรหัสโปรโมชั่นเพื่อรับเครดิตฟรี", "Enter your promo code to receive free credits")}
                        </p>
                    </div>

                    {/* Main Card */}
                    <Card variant="glass" className="shadow-2xl">
                        <CardHeader className="text-center pb-2">
                            <CardTitle className="flex items-center justify-center gap-2">
                                <Ticket className="h-5 w-5 text-primary" />
                                {t("กรอกรหัสคูปอง", "Enter Coupon Code")}
                            </CardTitle>
                            <CardDescription>
                                {t("รหัสคูปองไม่เรียงตัวพิมพ์ใหญ่-เล็ก", "Coupon code is case-insensitive")}
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6 pt-4">
                            {/* Input */}
                            <div className="space-y-4">
                                <Input
                                    value={couponCode}
                                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                    placeholder={t("เช่น NEWYEAR2026", "e.g. NEWYEAR2026")}
                                    className="text-center text-2xl font-mono tracking-widest h-14 uppercase"
                                    onKeyDown={(e) => e.key === "Enter" && handleRedeem()}
                                    disabled={isRedeeming}
                                />
                                <Button
                                    variant="hero"
                                    size="lg"
                                    className="w-full gap-2"
                                    onClick={handleRedeem}
                                    disabled={isRedeeming || !couponCode.trim()}
                                >
                                    {isRedeeming ? (
                                        <>
                                            <Loader2 className="h-5 w-5 animate-spin" />
                                            {t("กำลังตรวจสอบ...", "Checking...")}
                                        </>
                                    ) : (
                                        <>
                                            <Sparkles className="h-5 w-5" />
                                            {t("รับเครดิต", "Redeem")}
                                        </>
                                    )}
                                </Button>
                            </div>

                            {/* Result */}
                            {redeemResult && (
                                <div
                                    className={`p-4 rounded-xl text-center ${redeemResult.success
                                            ? "bg-green-500/10 border border-green-500/30"
                                            : "bg-red-500/10 border border-red-500/30"
                                        }`}
                                >
                                    {redeemResult.success ? (
                                        <>
                                            <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-2" />
                                            <p className="text-lg font-bold text-green-600 dark:text-green-400">
                                                {t("สำเร็จ!", "Success!")}
                                            </p>
                                            <p className="text-3xl font-bold mt-2 text-gradient">
                                                +{redeemResult.credits} {t("เครดิต", "Credits")}
                                            </p>
                                            <Link to="/dashboard" className="inline-block mt-4">
                                                <Button variant="outline" className="gap-2">
                                                    <CreditCard className="h-4 w-4" />
                                                    {t("ไปที่ Dashboard", "Go to Dashboard")}
                                                </Button>
                                            </Link>
                                        </>
                                    ) : (
                                        <>
                                            <p className="text-red-600 dark:text-red-400 font-medium">
                                                {redeemResult.message}
                                            </p>
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Login prompt */}
                            {!user && !authLoading && (
                                <div className="text-center p-4 bg-muted/50 rounded-xl">
                                    <p className="text-muted-foreground mb-3">
                                        {t("คุณต้องเข้าสู่ระบบก่อนใช้คูปอง", "You need to login first")}
                                    </p>
                                    <Link to="/login">
                                        <Button variant="outline" className="gap-2">
                                            {t("เข้าสู่ระบบ", "Login")}
                                            <ArrowRight className="h-4 w-4" />
                                        </Button>
                                    </Link>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Info */}
                    <div className="mt-8 text-center text-sm text-muted-foreground">
                        <p>{t("ได้รับรหัสคูปองจากคอร์สเรียนหรือโปรโมชั่น?", "Got a coupon code from courses or promotions?")}</p>
                        <p>{t("กรอกรหัสด้านบนเพื่อรับเครดิตฟรี!", "Enter the code above to get free credits!")}</p>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default Promo;
